import React, { useState, useEffect } from 'react';
import { useStores, invalidateStore } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import {
  LayoutDashboard,
  Calendar,
  Users,
  CheckSquare,
  MapPin,
  DollarSign,
  Receipt,
  Bot,
  CheckCircle2,
  Award,
  RefreshCw,
  Package,
  UtensilsCrossed,
  Activity,
  Phone,
  ExternalLink
} from 'lucide-react';

import { money, percent, isOverdue } from '../utils/formatters';

const DASHBOARD_STORES = ['events', 'venues', 'attendees', 'schedule', 'tasks', 'budget', 'meals', 'inventory'];

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [lastUpdated, setLastUpdated] = useState('');

  // All store data comes from the shared in-memory cache — zero extra Supabase
  // calls when navigating back to Dashboard after visiting another page.
  const { events, venues, attendees, schedule, tasks, budget, meals, inventory, loading } =
    useStores(DASHBOARD_STORES);

  // Update "last synced" timestamp whenever loading finishes
  useEffect(() => {
    if (!loading) {
      setLastUpdated(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    }
  }, [loading]);

  const triggerLiveSync = () => {
    // Force a fresh fetch from Supabase for all stores
    invalidateStore();
  };

  // 1. STATS CALCULATION
  const hotels = venues.filter((v) => (v.venueType || '').toLowerCase() === 'hotel').length;
  const restaurants = venues.filter((v) => (v.venueType || '').toLowerCase() === 'restaurant').length;
  const conferenceRooms = venues.filter((v) => (v.venueType || '').toLowerCase() === 'conference').length;

  const totalAttendees = attendees.length;
  const confirmedAttendees = attendees.filter((a) => a.rsvp === 'confirmed').length;
  const pendingAttendees = attendees.filter((a) => (a.rsvp || 'pending') === 'pending').length;
  const roomNeeded = attendees.filter((a) => a.roomNeeded).length;

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((t) => t.status === 'done').length;
  const overdueTasksCount = tasks.filter(isOverdue).length;
  const blockedTasksCount = tasks.filter((t) => t.status === 'blocked').length;

  const expenses = budget.filter((b) => b.type === 'expense');
  const totalSpent = expenses.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
  const totalEstimatedSpent = expenses.reduce((sum, b) => sum + (parseFloat(b.estimated) || parseFloat(b.amount) || 0), 0);
  const missingProofs = expenses.filter((e) => !(e.attachments || []).length);

  const totalTravelCostValue = attendees.reduce((sum, a) => sum + (parseFloat(a.travelCost) || 0), 0);
  const travelBookedCount = attendees.filter((a) => ['booked', 'issued', 'completed'].includes(a.travelStatus)).length;
  const travelPendingCount = attendees.filter((a) => (a.travelStatus || 'pending') === 'pending').length;

  // Let's compute Readiness Score similar to AskPlanner logic
  const publishedSessions = schedule.filter((s) => s.published).length;
  
  const rsvpPercentage = percent(confirmedAttendees, Math.max(totalAttendees, events[0]?.expectedAttendees || 1));
  const travelPercentage = percent(travelBookedCount, totalAttendees || 1);
  const tasksPercentage = percent(completedTasks, totalTasks || 1);
  const schedulePercentage = percent(publishedSessions, schedule.length || 1);
  const compliancePercentage = percent(expenses.length - missingProofs.length, expenses.length || 1);

  const readinessScoreValue = Math.round((
    rsvpPercentage +
    travelPercentage +
    tasksPercentage +
    schedulePercentage +
    compliancePercentage
  ) / 5) || 0;

  // Key POC list compiled
  const contacts = [];
  events.forEach((event) => {
    if (event.primaryPocName) {
      contacts.push({ source: 'Event Plan', context: event.name, name: event.primaryPocName, phone: event.primaryPocPhone, email: event.primaryPocEmail });
    }
  });
  venues.forEach((venue) => {
    if (venue.contactName) {
      contacts.push({ source: venue.venueType || 'Venue', context: venue.name, name: venue.contactName, phone: venue.contactPhone, email: venue.contactEmail });
    }
  });
  schedule.forEach((s) => {
    if (s.pocName && !contacts.some(c => c.name === s.pocName)) {
      contacts.push({ source: 'Session', context: s.title, name: s.pocName, phone: s.pocPhone });
    }
  });

  if (loading) return <PageLoader label="Loading your dashboard…" sublabel="Gathering the latest across your workspace" />;

  return (
    <div style={{ animation: 'fade-in 0.3s ease-out' }}>
      
      {/* 2. PAGE HEADER */}
      <div className="page-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Activity className="text-primary" size={28} style={{ color: 'var(--accent)', flexShrink: 0 }} />
            <span>Meetup Command Center</span>
          </div>
          <div className="page-subtitle">
            Operations & dashboard for 3-4 day corporate offsite meetup projects
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {lastUpdated && (
            <div style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--font-mono)' }}>
              Last sync: {lastUpdated}
            </div>
          )}
          <button 
            className="btn btn-secondary" 
            onClick={triggerLiveSync} 
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Sync Live</span>
          </button>
        </div>
      </div>

      {/* 4. SHADCN STATS GRID */}
      <div className="sh-stat-grid">
        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Readiness Score</span>
            <Award size={18} style={{ color: 'var(--accent)' }} />
          </div>
          <div className="sh-stat-value">{readinessScoreValue}%</div>
          <div className="sh-stat-desc" style={{ marginTop: 4 }}>
            <div className="sh-progress" style={{ height: 6 }}>
              <div 
                className="sh-progress-fill" 
                style={{ 
                  width: `${readinessScoreValue}%`, 
                  background: readinessScoreValue > 80 ? 'var(--success)' : readinessScoreValue > 50 ? 'var(--accent)' : 'var(--danger)'
                }} 
              />
            </div>
          </div>
        </div>

        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">RSVP Status</span>
            <Users size={18} style={{ color: 'var(--accent2)' }} />
          </div>
          <div className="sh-stat-value">{confirmedAttendees} / {totalAttendees}</div>
          <div className="sh-stat-desc">
            {percent(confirmedAttendees, totalAttendees)}% confirmed RSVP rate
          </div>
        </div>

        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Budget Ledger</span>
            <DollarSign size={18} style={{ color: '#166534' }} />
          </div>
          <div className="sh-stat-value" style={{ fontSize: 24 }}>{money(totalSpent)}</div>
          <div className="sh-stat-desc">
            Spent of estimated {money(totalEstimatedSpent)}
          </div>
        </div>

        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Tasks Progress</span>
            <CheckSquare size={18} style={{ color: 'var(--success)' }} />
          </div>
          <div className="sh-stat-value">{completedTasks} / {totalTasks}</div>
          <div className="sh-stat-desc">
            {percent(completedTasks, totalTasks)}% task checklists resolved
          </div>
        </div>
      </div>

      {/* 5. TABS NAVIGATION Bar */}
      <div className="sh-tabs-list">
        <button 
          className={`sh-tabs-trigger ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <LayoutDashboard size={14} />
          <span>Overview</span>
        </button>
        <button 
          className={`sh-tabs-trigger ${activeTab === 'logistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('logistics')}
        >
          <Users size={14} />
          <span>Logistics & Travel</span>
        </button>
        <button 
          className={`sh-tabs-trigger ${activeTab === 'financials' ? 'active' : ''}`}
          onClick={() => setActiveTab('financials')}
        >
          <DollarSign size={14} />
          <span>Financials & Inventory</span>
        </button>
        <button 
          className={`sh-tabs-trigger ${activeTab === 'operations' ? 'active' : ''}`}
          onClick={() => setActiveTab('operations')}
        >
          <CheckSquare size={14} />
          <span>Action & Audit Items</span>
        </button>
      </div>

      {/* 6. TABS CONTENT */}
      
      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div className="grid-3" style={{ gap: 16 }}>
          {/* Card Left: Recent Projects / Events info */}
          <div className="sh-card" style={{ gridColumn: 'span 2', minWidth: 0 }}>
            <div className="sh-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="sh-card-title">Event Roadmap & Schedules</div>
                <div className="sh-card-description">
                  {hotels} hotel(s), {restaurants} restaurant(s) & {conferenceRooms} venue(s) tracked
                </div>
              </div>
              <span className="sh-badge sh-badge-outline">{events.length} Plans</span>
            </div>
            
            {events.length === 0 ? (
              <div className="empty-state">
                <Calendar className="empty-icon" size={28} />
                <h3>No registered events</h3>
                <p>Add events under the Events project tab.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {events.map((ev) => {
                  return (
                    <div 
                      key={ev.id} 
                      style={{ 
                        border: '1px solid #f4f4f5', 
                        borderRadius: 8, 
                        padding: 16, 
                        background: 'linear-gradient(to right, #ffffff, #fafafa)' 
                      }}
                    >
                      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <strong style={{ fontSize: 16, color: '#09090b' }}>{ev.name}</strong>
                          <div style={{ fontSize: 13, color: '#71717a', marginTop: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Calendar size={13} /> {ev.date || 'TBD'} {ev.endDate ? `to ${ev.endDate}` : ''}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={13} /> {ev.venue || 'TBD'}</span>
                          </div>
                        </div>
                        <span className={`sh-badge ${ev.status === 'confirmed' ? 'sh-badge-success' : 'sh-badge-warning'}`} style={{ flexShrink: 0 }}>
                          {ev.status || 'planning'}
                        </span>
                      </div>
                      
                      {ev.notes && (
                        <p style={{ fontSize: 13, color: '#4b5563', marginTop: 10, fontStyle: 'italic', borderLeft: '2px solid var(--accent)', paddingLeft: 8 }}>
                          "{ev.notes}"
                        </p>
                      )}

                      {/* Brief micro timeline of draft itinerary */}
                      {schedule.length > 0 && (() => {
                        const sorted = [...schedule].sort((a, b) => {
                          const ta = (a.startTime || '').replace(':', '');
                          const tb = (b.startTime || '').replace(':', '');
                          return ta.localeCompare(tb);
                        });
                        return (
                          <div style={{ marginTop: 14 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text2)', marginBottom: 6 }}>
                              Day 1 Highlights:
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {sorted.slice(0, 2).map((item) => (
                                <div key={item.id} style={{ display: 'flex', flexWrap: 'wrap', fontSize: 12, padding: '4px 6px', background: '#f9f9fb', borderRadius: 4, gap: 4 }}>
                                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--accent)', flexShrink: 0 }}>{item.startTime || '--:--'}</span>
                                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{item.title}</span>
                                  <span style={{ color: 'var(--text2)', fontSize: 11, flexShrink: 0 }}>{item.location || item.conferenceRoom || 'Room TBD'}</span>
                                </div>
                              ))}
                            </div>
                            <button
                              onClick={() => window.dispatchEvent(new CustomEvent('planner:navigate', { detail: 'schedule' }))}
                              style={{ marginTop: 8, fontSize: 11, color: 'var(--accent)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                              <ExternalLink size={11} /> View full itinerary
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="sh-card-header" style={{ marginTop: 24, marginBottom: 12 }}>
              <div className="sh-card-title" style={{ fontSize: 16 }}>Key On-Ground POCs</div>
              <div className="sh-card-description">Venue, Event, and session operators for direct contact</div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {contacts.slice(0, 4).map((c, i) => (
                <div key={i} className="sh-list-item" style={{ borderRadius: 6, border: '1px solid #f4f4f5', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase' }} className="sh-badge-outline sh-badge">
                      {c.source}
                    </span>
                    <strong style={{ marginLeft: 8, fontSize: 13 }}>{c.name}</strong>
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{c.context}</div>
                  </div>
                  <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong style={{ fontSize: 12, fontFamily: 'var(--font-mono)' }}>{c.phone || 'No phone recorded'}</strong>
                    {c.phone && (
                      <a
                        href={`tel:${c.phone}`}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: 'var(--success)', color: '#fff', flexShrink: 0 }}
                        title={`Call ${c.name}`}
                      >
                        <Phone size={13} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Card Right: Live Launch Checklist */}
          <div className="sh-card">
            <div className="sh-card-header">
              <div className="sh-card-title">Launch Readiness</div>
              <div className="sh-card-description">Live system audit progress checklist</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{ display: 'inline-flex', position: 'relative', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ fontSize: 42, fontWeight: 800, color: readinessScoreValue > 85 ? 'var(--success)' : 'var(--accent)' }}>
                    {readinessScoreValue}%
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                  Overall Operations Readiness Index
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircle2 size={16} style={{ color: events.length > 0 ? 'var(--success)' : 'var(--text3)' }} />
                    <span>Event Plans Registered</span>
                  </div>
                  <span className={`sh-badge ${events.length > 0 ? 'sh-badge-success' : 'sh-badge-outline'}`}>
                    {events.length > 0 ? 'Done' : 'Pending'}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircle2 size={16} style={{ color: rsvpPercentage >= 80 ? 'var(--success)' : 'var(--text3)' }} />
                    <span>Attendee RSVP Confirmed</span>
                  </div>
                  <span className={`sh-badge ${rsvpPercentage >= 80 ? 'sh-badge-success' : 'sh-badge-warning'}`}>
                    {confirmedAttendees} confirmed
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircle2 size={16} style={{ color: travelPendingCount === 0 ? 'var(--success)' : 'var(--text3)' }} />
                    <span>Logistics Booked</span>
                  </div>
                  <span className={`sh-badge ${travelPendingCount === 0 ? 'sh-badge-success' : 'sh-badge-warning'}`}>
                    {travelPendingCount} pending
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircle2 size={16} style={{ color: overdueTasksCount === 0 ? 'var(--success)' : 'var(--text3)' }} />
                    <span>Sprint Slippage Cleared</span>
                  </div>
                  <span className={`sh-badge ${overdueTasksCount === 0 ? 'sh-badge-success' : 'sh-badge-danger'}`}>
                    {overdueTasksCount} overdue
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircle2 size={16} style={{ color: missingProofs.length === 0 ? 'var(--success)' : 'var(--text3)' }} />
                    <span>Expenditures Audited</span>
                  </div>
                  <span className={`sh-badge ${missingProofs.length === 0 ? 'sh-badge-success' : 'sh-badge-danger'}`}>
                    {missingProofs.length} missing receipt
                  </span>
                </div>
              </div>

              <div style={{ background: '#f9f9fb', borderRadius: 8, padding: 12, border: '1px solid #e4e4e7', marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                  <Bot size={16} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <strong>Need immediate help?</strong> Use the floating assistant at the bottom right. Say <em>"Review risks"</em> or <em>"What tasks need attention?"</em> to auto-remedy gaps live!
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LOGISTICS TAB */}
      {activeTab === 'logistics' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Detailed stats grids */}
          <div className="sh-stat-grid">
            <div className="sh-stat-card">
              <div className="sh-stat-head">
                <span className="sh-stat-label">Confirmed RSVPs</span>
                <Users size={16} style={{ color: 'var(--success)' }} />
              </div>
              <div className="sh-stat-value">{confirmedAttendees} / {totalAttendees}</div>
              <div className="sh-stat-desc">{pendingAttendees} travelers pending reply</div>
            </div>
            <div className="sh-stat-card">
              <div className="sh-stat-head">
                <span className="sh-stat-label">Hotel Stay Rooms</span>
                <MapPin size={16} style={{ color: 'var(--accent)' }} />
              </div>
              <div className="sh-stat-value">{roomNeeded}</div>
              <div className="sh-stat-desc">For confirmed offsite employees</div>
            </div>
            <div className="sh-stat-card">
              <div className="sh-stat-head">
                <span className="sh-stat-label">Travel Booked</span>
                <RefreshCw size={16} style={{ color: 'var(--accent2)' }} />
              </div>
              <div className="sh-stat-value">{travelBookedCount} / {totalAttendees}</div>
              <div className="sh-stat-desc">{travelPendingCount} pending ticket booking</div>
            </div>
            <div className="sh-stat-card">
              <div className="sh-stat-head">
                <span className="sh-stat-label">Total Airline/Cab Cost</span>
                <DollarSign size={16} style={{ color: '#166534' }} />
              </div>
              <div className="sh-stat-value" style={{ fontSize: 24 }}>{money(totalTravelCostValue)}</div>
              <div className="sh-stat-desc">Corporate card travel ledger limit</div>
            </div>
          </div>

          <div className="sh-card">
            <div className="sh-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="sh-card-title">Attendee Travel & Accommodation Roster</div>
                <div className="sh-card-description">Direct tracking of corporate travel codes, flight PNR, taxi bookings and hotel stay</div>
              </div>
              <span className="sh-badge sh-badge-outline">{attendees.length} Attendees</span>
            </div>

            {attendees.length === 0 ? (
              <div className="empty-state">
                <Users className="empty-icon" size={28} />
                <h3>Roster holds no occupants</h3>
                <p>Register attendees inside the Attendees section.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Attendee</th>
                      <th>Dept/Role</th>
                      <th>Origin</th>
                      <th>Transit Plan</th>
                      <th>Travel Status</th>
                      <th>Travel Booking Details</th>
                      <th>Stay</th>
                      <th>Expense Budget</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendees.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <strong>{a.name}</strong>
                          <div style={{ fontSize: 11, color: 'var(--text2)' }}>{a.email}</div>
                        </td>
                        <td>{a.role || 'Engineering'}</td>
                        <td>{a.fromCity || 'TBD'}</td>
                        <td>
                          <span className={`sh-badge sh-badge-outline`} style={{ fontSize: 10 }}>
                            {a.travelMode || 'TBD'}
                          </span>
                        </td>
                        <td>
                          <span className={`sh-badge ${
                            a.travelStatus === 'booked' || a.travelStatus === 'completed' || a.travelStatus === 'issued'
                              ? 'sh-badge-success'
                              : 'sh-badge-warning'
                          }`}>
                            {a.travelStatus || 'pending'}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontSize: 13 }}>
                            {a.flightPnr ? (
                              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, background: '#f4f4f5', padding: '2px 4px', borderRadius: 4 }}>
                                PNR: {a.flightPnr}
                              </span>
                            ) : a.cabBookingRef ? (
                              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, background: '#f4f4f5', padding: '2px 4px', borderRadius: 4 }}>
                                CAB: {a.cabBookingRef}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--text3)' }}>No references</span>
                            )}
                          </div>
                          {a.flightNo && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>Route: {a.flightNo}</div>}
                        </td>
                        <td>{a.roomNeeded ? '🏨 Stay Booked' : '❌ No room req.'}</td>
                        <td><strong>{money(a.travelCost)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FINANCIALS & INVENTORY TAB */}
      {activeTab === 'financials' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          
          {/* Top detailed spent bars */}
          <div className="grid-2" style={{ gap: 16 }}>
            <div className="sh-card">
              <div className="sh-card-title">Corporate Budget Spend Rate</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                <span>Budget Spent: <strong>{money(totalSpent)}</strong></span>
                <span>Estimated Target: <strong>{money(totalEstimatedSpent)}</strong></span>
              </div>
              <div className="sh-progress" style={{ height: 12, marginBottom: 12 }}>
                <div 
                  className="sh-progress-fill" 
                  style={{ 
                    width: `${Math.min(percent(totalSpent, totalEstimatedSpent), 100)}%`, 
                    background: totalSpent > totalEstimatedSpent ? 'var(--danger)' : 'var(--accent)'
                  }} 
                />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text2)' }}>
                {totalSpent > totalEstimatedSpent 
                  ? '⚠️ Expenditures have exceeded the target allocation estimations.' 
                  : `Currently running at ${percent(totalSpent, totalEstimatedSpent)}% of target estimations.`
                }
              </p>
            </div>

            <div className="sh-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div className="sh-card-title" style={{ marginBottom: 8 }}>Audit Compliance Checker</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: missingProofs.length > 0 ? 'var(--danger)' : 'var(--success)' }}>
                    {missingProofs.length}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>Missing billing receipts</div>
                </div>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--warning)' }}>
                    {expenses.filter(e => ['pending', 'disputed'].includes(e.status)).length}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>Vouchers in queue review</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid-2" style={{ gap: 16 }}>
            {/* budget records block */}
            <div className="sh-card">
              <div className="sh-card-header">
                <div className="sh-card-title">Recent Event Vouchers & Receipts</div>
                <div className="sh-card-description">Card payments, supplier invoices and cash reimbursements</div>
              </div>

              {budget.length === 0 ? (
                <div className="empty-state">
                  <Receipt className="empty-icon" size={28} />
                  <h3>No recorded line items</h3>
                  <p>Check the Budget section to add corporate card expenditures.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {budget.slice(0, 5).map((b) => (
                    <div key={b.id} className="sh-list-item" style={{ border: '1px solid #f4f4f5', borderRadius: 8, padding: 10 }}>
                      <div>
                        <strong style={{ fontSize: 13 }}>{b.title}</strong>
                        <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <span style={{ textTransform: 'capitalize' }}>Type: {b.type}</span>
                          <span>•</span>
                          <span>Cat: {b.category || 'misc'}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: b.type === 'income' ? 'var(--success)' : '#09090b' }}>
                          {b.type === 'income' ? '+' : '-'}{money(b.amount)}
                        </div>
                        <span className={`sh-badge ${
                          b.status === 'approved' || b.status === 'paid' ? 'sh-badge-success' : b.status === 'pending' ? 'sh-badge-warning' : 'sh-badge-danger'
                        }`} style={{ fontSize: 9, padding: '1px 4px', marginTop: 2 }}>
                          {b.status || 'pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* inventory supplies registry */}
            <div className="sh-card">
              <div className="sh-card-header">
                <div className="sh-card-title">Logistics Supplies & Inventory</div>
                <div className="sh-card-description">Operational hardware, customized company swag, and branding gears</div>
              </div>

              {inventory.length === 0 ? (
                <div className="empty-state">
                  <Package className="empty-icon" size={28} />
                  <h3>Supplies registry empty</h3>
                  <p>Input items in the Inventory tab.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {inventory.slice(0, 5).map((item) => (
                    <div key={item.id} className="sh-list-item" style={{ border: '1px solid #f4f4f5', borderRadius: 8, padding: 10 }}>
                      <div>
                        <strong style={{ fontSize: 13 }}>{item.name}</strong>
                        <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', gap: 6, marginTop: 2 }}>
                          <span>Qty: {item.quantity || 1} {item.unit || 'pcs'}</span>
                          <span>•</span>
                          <span>Owner: {item.assignedTo || 'Unassigned'}</span>
                        </div>
                      </div>
                      <div>
                        <span className={`sh-badge ${
                          item.status === 'received' ? 'sh-badge-success' : item.status === 'ordered' ? 'sh-badge-info' : 'sh-badge-warning'
                        }`}>
                          {item.status || 'needed'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* OPERATIONS & TASKS TAB */}
      {activeTab === 'operations' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          
          <div className="sh-stat-grid">
            <div className="sh-stat-card">
              <div className="sh-stat-head">
                <span className="sh-stat-label">Tasks Done</span>
                <CheckSquare size={16} style={{ color: 'var(--success)' }} />
              </div>
              <div className="sh-stat-value">{completedTasks} / {totalTasks}</div>
              <div className="sh-stat-desc">{totalTasks - completedTasks} left, {blockedTasksCount} blocked task(s)</div>
            </div>
            <div className="sh-stat-card">
              <div className="sh-stat-head">
                <span className="sh-stat-label">Overdue Deadlines</span>
                <CheckSquare size={16} style={{ color: 'var(--danger)' }} />
              </div>
              <div className="sh-stat-value" style={{ color: overdueTasksCount > 0 ? 'var(--danger)' : '#09090b' }}>
                {overdueTasksCount}
              </div>
              <div className="sh-stat-desc">Urgent sprints needing immediate care</div>
            </div>
            <div className="sh-stat-card">
              <div className="sh-stat-head">
                <span className="sh-stat-label">Catering Meal Plans</span>
                <UtensilsCrossed size={16} />
              </div>
              <div className="sh-stat-value">{meals.length}</div>
              <div className="sh-stat-desc">Covers breakfasts, welcome dinner, breaks</div>
            </div>
          </div>

          <div className="grid-2" style={{ gap: 16 }}>
            {/* Sprints Todo card */}
            <div className="sh-card">
              <div className="sh-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div className="sh-card-title">Urgent Operations Checklist</div>
                  <div className="sh-card-description">Critically high, overdue and blocked project milestones</div>
                </div>
                <span className="sh-badge sh-badge-danger">Sprint Focus</span>
              </div>

              {tasks.length === 0 ? (
                <div className="empty-state">
                  <CheckSquare className="empty-icon" size={28} />
                  <h3>Sprint board clear</h3>
                  <p>All tasks completed successfully or empty.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {tasks
                    .filter((t) => t.status !== 'done')
                    .slice(0, 5)
                    .map((t) => {
                      const overdue = isOverdue(t);
                      return (
                        <div 
                          key={t.id} 
                          style={{ 
                            border: '1px solid #f4f4f5', 
                            borderRadius: 8, 
                            padding: 12, 
                            background: overdue ? '#fff8f8' : t.status === 'blocked' ? '#fffdf5' : '#ffffff',
                            borderColor: overdue ? '#fca5a5' : t.status === 'blocked' ? '#fcd34d' : '#f4f4f5'
                          }}
                        >
                          <div style={{ display: 'flex', justifyBetween: 'space-between', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <strong style={{ fontSize: 13, color: '#09090b' }}>{t.title}</strong>
                            <span className={`sh-badge ${overdue ? 'sh-badge-danger' : t.status === 'blocked' ? 'sh-badge-warning' : 'sh-badge-outline'}`} style={{ fontSize: 9 }}>
                              {overdue ? 'OVERDUE' : t.status || 'todo'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyBetween: 'space-between', fontSize: 11, color: 'var(--text2)', marginTop: 6, justifyContent: 'space-between' }}>
                            <span>Deliver to: <strong>{t.assignedTo || 'Unassigned'}</strong></span>
                            <span>Target: <strong style={{ color: overdue ? 'var(--danger)' : 'inherit' }}>{t.dueDate || 'No due date'}</strong></span>
                          </div>
                        </div>
                      );
                    })}
                  {tasks.filter((t) => t.status !== 'done').length === 0 && (
                    <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 13, color: 'var(--success)', fontWeight: 600 }}>
                      🎉 Excellent job! All checklists and corporate sprints are resolved.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Catering meals card */}
            <div className="sh-card">
              <div className="sh-card-header">
                <div className="sh-card-title">Catering & Food Plans</div>
                <div className="sh-card-description">Meal rosters, caterer rates and food counts tracking</div>
              </div>

              {meals.length === 0 ? (
                <div className="empty-state">
                  <UtensilsCrossed className="empty-icon" size={28} />
                  <h3>No catering recorded</h3>
                  <p>Input catering details inside the Meals section.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {meals.slice(0, 5).map((meal) => {
                    const totalHeads = (parseInt(meal.vegCount, 10) || 0) + (parseInt(meal.nonVegCount, 10) || 0);
                    const totalCost = (parseFloat(meal.pricePerHead) || 0) * totalHeads;
                    return (
                      <div key={meal.id} className="sh-list-item" style={{ border: '1px solid #f4f4f5', borderRadius: 8, padding: 10 }}>
                        <div>
                          <strong style={{ fontSize: 13 }}>{meal.mealType} at {meal.vendor || 'TBD'}</strong>
                          <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', gap: 6, marginTop: 2, alignItems: 'center' }}>
                            <span>Veg: {meal.vegCount || 0}</span>
                            <span>•</span>
                            <span>Non-veg: {meal.nonVegCount || 0}</span>
                            <span>•</span>
                            <span>Day: {meal.day || 'TBD'}</span>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <strong style={{ fontSize: 12 }}>{money(totalCost)}</strong>
                          <div style={{ fontSize: 9, color: 'var(--text3)' }}>{money(meal.pricePerHead)}/head</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

